/**
 * MultiSplitAssignModal (Feb 2026)
 * --------------------------------
 * Reusable confirmation modal that lets a supervisor pick multiple
 * pickers and split a batch of picking / put-away tasks evenly between
 * them. The split uses the SAME alphabetical-by-`location_id` ordering
 * the printed Pick List + Floor Mode picking view already use, so each
 * picker receives a contiguous block of locations and the three views
 * agree row-for-row.
 *
 * Powered by the backend endpoints:
 *   POST /api/admin/warehouse/tasks/split-preview  (dry-run, no writes)
 *   POST /api/admin/warehouse/tasks/split-assign   (writes)
 *
 * Usage (from WmsTaskAssignPanel or AdminGoodsReceiving):
 *   <MultiSplitAssignModal
 *     open={showSplit}
 *     onClose={() => setShowSplit(false)}
 *     scope="order"   // 'order' | 'asn'
 *     orderId={orderId}
 *     type="picking"
 *     users={assignableUsers}
 *     API={API}
 *     authHeaders={authHeaders}
 *     onAssigned={reloadParent}
 *   />
 *
 * The modal has three phases:
 *   1. PICK — supervisor checks 2+ pickers from the users list.
 *   2. PREVIEW — hit /split-preview and render the per-picker block
 *      (location-from .. location-to + task count). Supervisor can go
 *      back to Pick to adjust.
 *   3. COMMIT — Confirm button hits /split-assign and toasts the
 *      summary ("3 pickers · 24 tasks assigned").
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { X, Users, ArrowRight, Check, Loader2, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const MultiSplitAssignModal = ({
  open,
  onClose,
  scope,          // 'order' | 'asn'
  orderId,
  asnId,
  type,           // 'picking' | 'putaway' — required for scope='order'
  users = [],
  API,
  authHeaders,
  onAssigned,
}) => {
  const { toast } = useToast();
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null); // { total, blocks: [...] }
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const rank = { manager: 0, floor_incharge: 1, picker: 2, viewer: 3 };
      const ra = rank[a.role] ?? 9;
      const rb = rank[b.role] ?? 9;
      if (ra !== rb) return ra - rb;
      return (a.name || a.email).localeCompare(b.name || b.email);
    });
  }, [users]);

  const toggle = (email) => {
    setPreview(null);
    setSelected((s) => s.includes(email) ? s.filter((e) => e !== email) : [...s, email]);
  };

  const buildBody = () => ({
    scope,
    order_id: scope === 'order' ? orderId : undefined,
    asn_id: scope === 'asn' ? asnId : undefined,
    type: scope === 'order' ? type : undefined,
    emails: selected,
  });

  const previewSplit = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/warehouse/tasks/split-preview`, buildBody(), { headers: authHeaders });
      setPreview(r.data);
    } catch (e) {
      toast({ title: 'Preview failed', description: e.response?.data?.detail || 'Try again.' });
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const r = await axios.post(`${API}/admin/warehouse/tasks/split-assign`, buildBody(), { headers: authHeaders });
      // 2026-10 — Only reflect what the server CONFIRMED persisted.
      // The backend now returns `verified_blocks` + `skipped_task_ids`
      // so we can warn the supervisor if any tasks fell out of the
      // split (completed mid-flight, deleted, or raced). We still
      // call `onAssigned` so the parent re-fetches authoritative
      // state from the DB rather than trusting our optimistic view.
      const assignedTotal = Number(r.data?.assigned_total || 0);
      const skipped = Array.isArray(r.data?.skipped_task_ids)
        ? r.data.skipped_task_ids.length
        : 0;
      const pickerCount = r.data?.picker_count || selected.length;
      if (skipped > 0) {
        toast({
          title: `Assigned with ${skipped} skipped`,
          description: `${pickerCount} pickers · ${assignedTotal} tasks persisted. ${skipped} task${skipped === 1 ? '' : 's'} were not written (completed or deleted mid-split) and will NOT appear on any picker's device.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Assigned',
          description: `${pickerCount} pickers · ${assignedTotal} tasks.`,
        });
      }
      if (onAssigned) await onAssigned();
      onClose();
      setSelected([]);
      setPreview(null);
    } catch (e) {
      toast({ title: 'Assignment failed', description: e.response?.data?.detail || 'Try again.' });
    } finally {
      setCommitting(false);
    }
  };

  if (!open) return null;
  const canPreview = selected.length >= 1;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="multi-split-assign-modal"
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-sky-600" />
            <h2 className="text-lg font-bold text-neutral-900">
              Split {type || 'put-away'} tasks across pickers
            </h2>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="multi-split-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Phase 1 — Pick */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">
              1. Select pickers ({selected.length})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {sortedUsers.map((u) => {
                const on = selected.includes(u.email);
                return (
                  <button
                    key={u.email}
                    type="button"
                    onClick={() => toggle(u.email)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${
                      on
                        ? 'bg-sky-50 border-sky-500 text-sky-900'
                        : 'bg-white border-neutral-200 hover:bg-neutral-50'
                    }`}
                    data-testid={`multi-split-picker-${u.email}`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? 'bg-sky-500 border-sky-500 text-white' : 'bg-white border-neutral-300'}`}>
                      {on && <Check className="w-3 h-3" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{u.name || u.email}</span>
                      <span className="block text-[10px] text-neutral-500 truncate">{u.role || '—'} · {u.email}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Phase 2 — Preview */}
          {preview && (
            <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">
                2. Preview — {preview.total} tasks across {preview.picker_count} pickers
              </div>
              {preview.total === 0 ? (
                <div className="text-sm text-neutral-500 italic">No open tasks to split.</div>
              ) : (
                <div className="space-y-2">
                  {preview.blocks.map((b, i) => (
                    <div
                      key={b.email}
                      className="flex items-center gap-2 bg-white border border-neutral-200 rounded-lg px-3 py-2"
                      data-testid={`multi-split-block-${b.email}`}
                    >
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-sky-600 text-white text-[11px] font-bold">
                        {i + 1}
                      </span>
                      <span className="font-semibold text-sm truncate flex-1" title={b.email}>{b.email}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-neutral-700 bg-neutral-100 rounded-full px-2 py-0.5">
                        <MapPin className="w-3 h-3" /> {b.location_from || '—'} <ArrowRight className="w-3 h-3" /> {b.location_to || '—'}
                      </span>
                      <span className="text-sm font-bold text-sky-700 min-w-[2.5rem] text-right">{b.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 p-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
            data-testid="multi-split-cancel"
          >
            Cancel
          </button>
          {!preview ? (
            <button
              type="button"
              onClick={previewSplit}
              disabled={!canPreview || loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="multi-split-preview-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Preview split
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                data-testid="multi-split-back-btn"
              >
                Edit pickers
              </button>
              <button
                type="button"
                onClick={commit}
                disabled={committing || preview.total === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="multi-split-confirm-btn"
              >
                {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Confirm assignment
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiSplitAssignModal;
